import React, { useState } from "react";
import "./workflowDms.css";

import {
  FiSettings,
  FiPackage,
  FiShoppingBag,
  FiUsers,
  FiArchive,
  FiMapPin,
  FiHome,
  FiShoppingCart,
  FiFileText,
  FiSend,
  FiTruck,
  FiTrendingUp,
  FiCreditCard,
  FiBarChart2,
  FiActivity,
  FiRefreshCw,
  FiArrowRight,
} from "react-icons/fi";
import DmsBookingModal from "./dmsBookingModal";

const lifecycleStages = [
  {
    number: "01",
    category: "SUPPLY CHAIN",
    title: "Production",
    icon: FiSettings,
    description:
      "Finished goods enter the distribution lifecycle and become ready for downstream movement.",
    capabilities: [
      "Finished Goods",
      "Production Planning",
      "Distribution Readiness",
    ],
  },
  {
    number: "02",
    category: "SUPPLY CHAIN",
    title: "Warehouse",
    icon: FiPackage,
    description:
      "Manage warehouse stock and product availability before goods move into the distribution channel.",
    capabilities: [
      "Warehouse Stock",
      "Stock Management",
      "Product Availability",
    ],
  },
  {
    number: "03",
    category: "PRIMARY SALES",
    title: "Primary Sales",
    icon: FiShoppingBag,
    description:
      "Move products from the manufacturer or warehouse into the distributor network.",
    capabilities: [
      "Primary Sales",
      "Distributor Orders",
      "Product Movement",
      "Sales Reporting",
    ],
  },
  {
    number: "04",
    category: "CHANNEL MANAGEMENT",
    title: "Distributor",
    icon: FiUsers,
    description:
      "Manage distributors, dealers and channel relationships from one connected platform.",
    capabilities: [
      "Distributor Management",
      "Dealer Management",
      "Distributor Hierarchy",
      "Territory Mapping",
      "Credit Limits",
      "Price Lists",
    ],
  },
  {
    number: "08",
    category: "ORDER MANAGEMENT",
    title: "Order Booking",
    icon: FiShoppingCart,
    description:
      "Capture retailer and distributor requirements digitally and move them into the order workflow.",
    capabilities: [
      "Order Booking",
      "Retailer Orders",
      "Distributor Orders",
      "Order Processing",
      "Order Visibility",
    ],
  },
  {
    number: "07",
    category: "RETAIL NETWORK",
    title: "Retailers",
    icon: FiHome,
    description:
      "Connect your sales team with retailers and outlets across the distribution network.",
    capabilities: [
      "Retailer Management",
      "Outlet Coverage",
      "Retailer Visits",
      "Retailer Engagement",
    ],
  },
  {
    number: "06",
    category: "FIELD SALES",
    title: "Sales Team",
    icon: FiMapPin,
    description:
      "Equip sales representatives with the tools they need for daily field execution.",
    capabilities: [
      "Live Orders",
      "Retailer Visits",
      "Beat Planning",
      "GPS Tracking",
      "Attendance",
      "Collections",
    ],
  },
  {
    number: "05",
    category: "INVENTORY MANAGEMENT",
    title: "Inventory",
    icon: FiArchive,
    description:
      "Know what stock is available, where it is available and how it is moving across the channel.",
    capabilities: [
      "Stock Ledger",
      "Stock Aging",
      "Stock Transfers",
      "Warehouse Stock",
      "Batch Tracking",
      "Expiry Alerts",
    ],
  },

  {
    number: "09",
    category: "BILLING",
    title: "Invoice",
    icon: FiFileText,
    description:
      "Generate invoices from processed orders while keeping billing connected to the distribution workflow.",
    capabilities: [
      "GST Invoice Generation",
      "E-Invoice",
      "E-Way Bill",
      "Invoice History",
      "Customer Ledger",
    ],
  },
  {
    number: "10",
    category: "FULFILLMENT",
    title: "Dispatch",
    icon: FiSend,
    description:
      "Move confirmed orders into dispatch while keeping fulfillment visible across the distribution process.",
    capabilities: [
      "Dispatch Processing",
      "Order Fulfillment",
      "Dispatch Visibility",
      "Transaction Tracking",
    ],
  },
  {
    number: "11",
    category: "FULFILLMENT",
    title: "Delivery",
    icon: FiTruck,
    description:
      "Complete delivery and keep the transaction connected to the rest of the distribution lifecycle.",
    capabilities: [
      "Delivery",
      "Delivery Confirmation",
      "Order Completion",
      "Transaction Visibility",
    ],
  },
  {
    number: "12",
    category: "SECONDARY SALES",
    title: "Secondary Sales",
    icon: FiTrendingUp,
    description:
      "Track how products move through distributors and retailers beyond the warehouse.",
    capabilities: [
      "Secondary Sales",
      "Sales Tracking",
      "Stock Reconciliation",
      "SKU-wise Sales",
      "Territory-wise Sales",
      "Distributor-wise Sales",
    ],
  },
  {
    number: "16",
    category: "CONTINUOUS GROWTH",
    title: "Repeat Sales",
    icon: FiRefreshCw,
    description:
      "The connected distribution lifecycle continues with repeat sales as businesses act on sales, inventory and demand visibility.",
    capabilities: [
      "Sales Trends",
      "Demand Visibility",
      "Product Performance",
      "Sales Planning",
    ],
  },

  {
    number: "15",
    category: "DEMAND PLANNING",
    title: "Forecasting",
    icon: FiActivity,
    description:
      "Use distribution data to support demand planning across routes, distributors and production.",
    capabilities: [
      "Route Demand Forecast",
      "Distributor Demand Planning",
      "Production Planning",
    ],
  },
  {
    number: "14",
    category: "BUSINESS INTELLIGENCE",
    title: "Business Intelligence",
    icon: FiBarChart2,
    description:
      "Turn distribution transactions into live dashboards and actionable business intelligence.",
    capabilities: [
      "Sales Analytics",
      "Inventory Reports",
      "Distributor Performance",
      "Retail Coverage",
      "Outstanding Payments",
      "Territory Performance",
    ],
  },
  {
    number: "13",
    category: "COLLECTIONS",
    title: "Collections",
    icon: FiCreditCard,
    description:
      "Bring outstanding payments and distributor collection activity into one connected view.",
    capabilities: [
      "Outstanding Invoices",
      "Payment Due Dates",
      "Collection Status",
      "Credit Limits",
      "Payment History",
      "Distributor Ledger",
    ],
  },
];

const WorkflowDms = () => {
  const [activeStage, setActiveStage] = useState(0);
  const [showDmsModal, setShowDmsModal] = useState(false);
  const active = lifecycleStages[activeStage];
  const ActiveIcon = active.icon;
  const nextStage =
    activeStage < lifecycleStages.length - 1
      ? lifecycleStages[activeStage + 1]
      : null;
  return (
    <>
      <section className="workflow-dms-section">
        <div
          className="workflow-dms-glow workflow-dms-glow-one"
          aria-hidden="true"
        />
        <div
          className="workflow-dms-glow workflow-dms-glow-two"
          aria-hidden="true"
        />
        <div className="workflow-dms-grid" aria-hidden="true" />
        <div className="container">
          <div
            className="workflow-dms-heading"
            data-aos="fade-up"
            data-aos-duration="850"
          >
            <div className="workflow-dms-label">
              COMPLETE DISTRIBUTION LIFECYCLE
            </div>
            <h2>
              Follow Your Distribution
              <br />
              <span>Journey From End to End</span>
            </h2>
            <p>
              Explore how products, orders, inventory and sales move through one
              connected distribution ecosystem.
            </p>
            <div className="workflow-dms-divider">
              <span className="workflow-dms-divider-small"></span>
              <span className="workflow-dms-divider-large"></span>
            </div>
          </div>

          <div className="workflow-dms-interactive">
            <div className="workflow-dms-info" key={active.number}>
              <div className="workflow-dms-info-top">
                <div className="workflow-dms-info-icon">
                  <ActiveIcon />
                </div>
                <div className="workflow-dms-info-meta">
                  <span className="workflow-dms-info-step">
                    STEP {active.number}
                  </span>
                  <span className="workflow-dms-info-category">
                    {active.category}
                  </span>
                </div>
              </div>
              <h3>{active.title}</h3>
              <p className="workflow-dms-info-description">
                {active.description}
              </p>
              <div className="workflow-dms-info-divider"></div>
              <span className="workflow-dms-info-label">KEY CAPABILITIES</span>
              <div className="workflow-dms-info-points">
                {active.capabilities.map((capability) => (
                  <div className="workflow-dms-info-point" key={capability}>
                    <span className="workflow-dms-info-point-dot"></span>
                    <span>{capability}</span>
                  </div>
                ))}
              </div>

              <div className="workflow-dms-info-next">
                {nextStage ? (
                  <>
                    <span>NEXT</span>
                    <strong>{nextStage.title}</strong>
                    <FiArrowRight />
                  </>
                ) : (
                  <>
                    <span>LIFECYCLE</span>
                    <strong>Complete</strong>
                    <FiRefreshCw />
                  </>
                )}
              </div>
            </div>

            <div className="workflow-dms-route-panel">
              <div className="workflow-dms-route-header">
                <div>
                  <span>DMS FLOW</span>
                  <strong>Hover or click a point to explore</strong>
                </div>
                <div className="workflow-dms-route-status">
                  <span></span>
                  CONNECTED FLOW
                </div>
              </div>
              <div className="workflow-dms-route">
                <svg
                  className="workflow-dms-route-svg"
                  viewBox="0 0 120 80"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <defs>
                    <filter
                      id="workflowDmsMarkerGlow"
                      x="-100%"
                      y="-100%"
                      width="300%"
                      height="300%"
                    >
                      <feGaussianBlur stdDeviation="1.2" result="blur" />
                      <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  <path
                    id="workflowDmsRoutePath"
                    className="workflow-dms-route-base"
                    d="
                                        M 15 10
                                        H 105

                                        Q 110 10 110 15
                                        V 25

                                        Q 110 30 105 30

                                        H 15

                                        Q 10 30 10 35
                                        V 45

                                        Q 10 50 15 50

                                        H 105

                                        Q 110 50 110 55
                                        V 65

                                        Q 110 70 105 70

                                        H 15
                                    "
                  />
                  <path
                    className="workflow-dms-route-active"
                    d="
                                        M 15 10
                                        H 105

                                        Q 110 10 110 15
                                        V 25

                                        Q 110 30 105 30

                                        H 15

                                        Q 10 30 10 35
                                        V 45

                                        Q 10 50 15 50

                                        H 105

                                        Q 110 50 110 55
                                        V 65

                                        Q 110 70 105 70

                                        H 15
                                    "
                  />
                  <circle className="workflow-dms-route-marker" r="1.15">
                    <animateMotion
                      dur="12s"
                      repeatCount="indefinite"
                      rotate="auto"
                    >
                      <mpath href="#workflowDmsRoutePath" />
                    </animateMotion>
                  </circle>

                  <circle className="workflow-dms-route-marker-glow" r="2.5">
                    <animateMotion
                      dur="12s"
                      repeatCount="indefinite"
                      rotate="auto"
                    >
                      <mpath href="#workflowDmsRoutePath" />
                    </animateMotion>
                  </circle>
                </svg>

                <div className="workflow-dms-route-points">
                  {lifecycleStages.map((stage, index) => {
                    const Icon = stage.icon;
                    return (
                      <button
                        key={stage.number}
                        type="button"
                        className={`
                                                workflow-dms-route-point
                                                ${
                                                  activeStage === index
                                                    ? "active"
                                                    : ""
                                                }
                                            `}
                        onMouseEnter={() => setActiveStage(index)}
                        onFocus={() => setActiveStage(index)}
                        onClick={() => setActiveStage(index)}
                        aria-label={`Explore ${stage.title}`}
                      >
                        <span className="workflow-dms-point-number">
                          {stage.number}
                        </span>

                        <span className="workflow-dms-point-circle">
                          <Icon />
                        </span>

                        <span className="workflow-dms-point-title">
                          {stage.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div
            className="workflow-dms-cta"
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-delay="200"
          >
            <button
              type="button"
              className="workflow-dms-cta-btn"
              onClick={() => setShowDmsModal(true)}
            >
              Speak with Our Experts
              <span>
                <FiArrowRight />
              </span>
            </button>
          </div>

          <div
            className="workflow-dms-bottom"
            data-aos="fade-up"
            data-aos-duration="700"
            data-aos-delay="300"
          >
            <div className="workflow-dms-bottom-icon">
              <FiTrendingUp />
            </div>
            <div className="workflow-dms-bottom-content">
              <strong>One connected distribution journey.</strong>
              <span>From supply chain to business intelligence.</span>
            </div>
          </div>
        </div>
      </section>
      <DmsBookingModal
        show={showDmsModal}
        onClose={() => setShowDmsModal(false)}
      />
    </>
  );
};

export default WorkflowDms;
